import { OnlineOrderConfirmationState, OnlineOrderItemFragment } from '@client/graphql/types/graphql';
import getImageLink from '@packages/core/image-source/get-image-link.ts';
import { useTranslate } from '@tolgee/react';
import moment from 'moment-timezone';
import { useState } from 'react';

export default function OnlineOrderManualTransferPanel(props: {
  order: OnlineOrderItemFragment;
  timezone: string;
}) {
  const { order, timezone } = props;
  const { t } = useTranslate();

  const [showFullProof, setShowFullProof] = useState(false);

  const hasProof = Boolean(order.manualTransferProofSubmittedAt);
  const isConfirmed = Boolean(order.manualTransferConfirmedAt);
  const wasRejected = Boolean(order.manualTransferRejectedAt);
  const isOrderDeclined = order.confirmationState === OnlineOrderConfirmationState.Canceled;

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <h3 className="text-base font-semibold mb-3">
        {t('online-order.manual-transfer.panel.title', 'Online Payment')}
      </h3>

      {isConfirmed && (
        <div className="space-y-3">
          <div className="rounded-md bg-green-50 border border-green-200 p-3 text-sm text-green-800">
            {t('online-order.manual-transfer.panel.confirmed', 'Payment was confirmed on {date}', {
              date: moment(order.manualTransferConfirmedAt).tz(timezone).format('HH:mm DD MMM YYYY'),
            })}
          </div>
          {order.manualTransferSenderName && (
            <p className="text-sm">
              <span className="text-gray-500">
                {t('online-order.manual-transfer.panel.sender', 'Sender')}:{' '}
              </span>
              <span className="font-medium">{order.manualTransferSenderName}</span>
            </p>
          )}
          {order.manualTransferProofImageOrigin && (
            <button
              type="button"
              onClick={() => setShowFullProof(true)}
              className="block rounded-lg border border-gray-200 overflow-hidden cursor-zoom-in"
            >
              <img
                src={getImageLink(order.manualTransferProofImageThumbnail ?? order.manualTransferProofImageOrigin)}
                alt="Proof"
                className="h-24 w-24 object-cover"
              />
            </button>
          )}
        </div>
      )}

      {!isConfirmed && hasProof && !isOrderDeclined && (
        <div className="space-y-3">
          <div className="rounded-md bg-yellow-50 border border-yellow-200 p-3 text-sm text-yellow-800">
            {t(
              'online-order.manual-transfer.panel.pending',
              'Customer uploaded a payment proof. Verify against your bank app before accepting the order.',
            )}
          </div>
          <p className="text-sm">
            <span className="text-gray-500">{t('online-order.manual-transfer.panel.sender', 'Sender')}: </span>
            <span className="font-medium">{order.manualTransferSenderName}</span>
          </p>
          <p className="text-sm">
            <span className="text-gray-500">
              {t('online-order.manual-transfer.panel.submitted-at', 'Submitted at')}:{' '}
            </span>
            <span className="font-medium">
              {moment(order.manualTransferProofSubmittedAt).tz(timezone).format('HH:mm DD MMM YYYY')}
            </span>
          </p>
          {order.manualTransferProofImageOrigin && (
            <button
              type="button"
              onClick={() => setShowFullProof(true)}
              className="block rounded-lg border border-gray-200 overflow-hidden cursor-zoom-in"
            >
              <img
                src={getImageLink(order.manualTransferProofImageThumbnail ?? order.manualTransferProofImageOrigin)}
                alt="Proof"
                className="h-24 w-24 object-cover"
              />
            </button>
          )}
        </div>
      )}

      {!isConfirmed && !hasProof && wasRejected && !isOrderDeclined && (
        <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-800">
          {t(
            'online-order.manual-transfer.panel.rejected',
            'Last proof was rejected on {date}: {reason}. Waiting for customer to re-upload.',
            {
              date: moment(order.manualTransferRejectedAt).tz(timezone).format('HH:mm DD MMM YYYY'),
              reason: order.manualTransferRejectionReason ?? '',
            },
          )}
        </div>
      )}

      {!isConfirmed && !hasProof && !wasRejected && !isOrderDeclined && (
        <div className="rounded-md bg-gray-50 border border-gray-200 p-3 text-sm text-gray-700">
          {t(
            'online-order.manual-transfer.panel.no-proof',
            'Customer has not uploaded payment proof yet. Contact them at {phone} if needed.',
            { phone: order.phone ?? '—' },
          )}
        </div>
      )}

      {!isConfirmed && isOrderDeclined && (
        <div className="rounded-md bg-gray-50 border border-gray-200 p-3 text-sm text-gray-700">
          {t(
            'online-order.manual-transfer.panel.order-declined',
            'Order has been declined. No further payment actions are available.',
          )}
        </div>
      )}

      {showFullProof && order.manualTransferProofImageOrigin && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => setShowFullProof(false)}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80 p-4 cursor-zoom-out"
        >
          <img
            src={getImageLink(order.manualTransferProofImageOrigin)}
            alt="Proof"
            className="max-h-full max-w-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            type="button"
            onClick={() => setShowFullProof(false)}
            className="absolute top-4 right-4 rounded-full bg-white/90 px-3 py-1 text-sm font-medium text-gray-900 hover:bg-white"
          >
            {t('common.button.close', 'Close')}
          </button>
        </div>
      )}
    </div>
  );
}
